/* jshint esversion: 6, -W097 */
/* globals module */
'use strict';

/**
 * CategoriesService is responsible for getting the list of categories
 *
 * @module enervee_utils.categories
 */

CategoriesService.$inject = [
    '$q',
    '$log',
    '$state',
    '$rootScope',
    'GetFetchService',
    'ProductApiConsumerService',
    'UtilService',
];


function CategoriesServiceProvider() {
    var provider = this;
    provider._waitForFormatters = false;

    /**
     * Used to prevent the `afterInitialCategoriesLoads` promise from
     * resolving immediately, allowing time to configure formatters and then manually initialize
     * the categories.
     *
     * @param {Boolean} val
     */
    provider.waitForFormatters = function (val) {
        provider._waitForFormatters = val;
    };

    provider.$get = CategoriesService;
}

function CategoriesService(
    $q,
    $log,
    $state,
    $rootScope,
    GetFetchService,
    ProductApiConsumerService,
    UtilService
) {
    var _pub = {},
        _categories = null,
        _orderedCategories = null,
        _parentCategories = null,
        _categoryObject = null,
        _activeCategory = null,
        _categoryIdToObject = {},
        _categoryCodeToId = {},
        _activeUtilityId = {},
        _initialCategoriesLoaded = $q.defer(),
        _formatters = [];

    _pub.afterInitializedCallback = null;

    /**
     * call  callback functions after categories initialized
     */
    function _doCallbacksAfterInitialized() {
       if (typeof _pub.afterInitializedCallback === "function") {
           _pub.afterInitializedCallback();
        }
    }

    /**
     * Helper function that takes a list of categories and performs the
     * following for each one, modifying in place:
     *
     * 1. Copies the category's `category_code` as `code`
     * 2. Places a reference to the category itself in an object using
     *    the category's id as the key
     * 3. Stores the category's id in an object using the category code
     *    as the key
     *
     * @param {Array} categories a list of category objects
     */
    var arrangeCategoriesByIdAndCategoryCode = function(categories) {
        categories.forEach(function (category) {
            category.code = category.category_code;
            _categoryIdToObject[category.id] = category;
            _categoryCodeToId[category.code] = category.id;
        });
    };

    /**
     * _applyFormatters isn't called here because there isn't time for addFormatter to have been
     * called yet.
     */
    if (!this._waitForFormatters) {
        _initialCategoriesLoaded.resolve();
    }

    /**
     * Returns a promise that can be used to wait for the initial
     * category data to load
     *
     * @return {Promise}
     */
    _pub.afterInitialCategoriesLoads = function() {
        return _initialCategoriesLoaded.promise;
    };

    _pub.finishInit = function () {
        _initialCategoriesLoaded.resolve();
    };

    /**
     * Adds a function to a list of functions which are used as a "post-processing" pipeline after
     * fetching categories.
     *
     * Adding formatters also requires setting `waitForFormatters(true)` using
     * CategoriesServiceProvider in order to prevent the `afterInitialCategoriesLoads` promise from
     * resolving too soon.
     *
     * @example
     * var formatter = function (categories) {
     *     categories.forEach(function (category)) {
     *         category.special_flag = true;
     *     }
     * }
     * CategoriesService.addFormatter(formatter);
     *
     * @param {Function} formatter A formatting function which, given an array of categories,
     * modifies the categories _in place_.
     */
    _pub.addFormatter = function (formatter) {
        if (!angular.isFunction(formatter)) {
            throw new Error('Expected formatter to be {Function}, but it isn\'t!');
        }
        _formatters.push(formatter);
    };

    /**
     * Applies the configured formatters to each category.
     * _NOTE_: categories are modified in place.
     *
     * @see {@link addFormatter}
     *
     * @param {Array<Object>} categories
     * @param {Array<Function>} [remainingFormatters]
     * @return {Promise}
     */
    function _applyFormatters(categories, remainingFormatters) {
        remainingFormatters = remainingFormatters || _formatters.slice(0);
        if (remainingFormatters.length === 0) return $q.resolve(categories);
        var formatter = remainingFormatters.shift();
        return $q.resolve(formatter(categories)).then(function () {
            return _applyFormatters(categories, remainingFormatters.slice(0));
        });
    }

    /**
     * Alias to call _applyFormatters with _categories.
     *
     * @return {Promise}
     */
    _pub.applyFormatters = function () {
        return _pub.getCategories().then(function () {
            return _applyFormatters(_categories);
        });
    };

    _pub.getCategoryIdByCode = function (code) {
        return _categoryCodeToId[code];
    };

    /**
     * Fetches the list of categories initially
     */
    _pub.getCategories = function (activeUtilityId) {
        var params = {};
        if (activeUtilityId) {
            params = { activeUtilityId: activeUtilityId };
        }

        var options = {
            'url': ProductApiConsumerService.getFullUrl('api/1.0/categories/'),
            'params': params,
            'method': 'GET'
        };

        return GetFetchService.get(['categories-for-utility', activeUtilityId], options).then(function (data) {
            _categories = data;
            arrangeCategoriesByIdAndCategoryCode(_categories);
            _pub.orderCategories();
            return _applyFormatters(_categories).then(function () {
                return _categories;
            });
        }, function (error) {
            $log.warn('Failed to get categories.');
            $state.go('404', {reason: error});
        });
    };

    _pub.orderCategories = function () {
        _orderedCategories = [];
        var categoryObject = {};
        var parentCategories = [];

        _categories.forEach(function (category) {
            category.spaceless_name = category.name.replace(/\s+/g, '-');
            _categoryIdToObject[category.id] = category;
            if (category.parent_category in categoryObject) {
                categoryObject[category.parent_category].push(category);
            } else {
                categoryObject[category.parent_category] = [category];
                parentCategories.push(category.parent_category);
            }
        });
        _categoryObject = categoryObject;
        parentCategories.sort();
        _parentCategories = parentCategories;
        parentCategories.forEach(function (parent_cat) {
            var parent_obj = {'name': parent_cat, 'is_parent': true};
            _orderedCategories.push(parent_obj);
            categoryObject[parent_cat].forEach(function (category) {
                _orderedCategories.push(category);
            });
        });

        _doCallbacksAfterInitialized();
        return _orderedCategories;
    };

    /**
     * Gets the default category by looping through the categories and
     * returning the first with `is_active: true`
     * @returns {*}
     */
    _pub.getDefaultCategory = function() {
        for (var i = 0; i < _categories.length; i++) {
            if (_categories[i].is_active) {
                return _categories[i];
            }
        }
        return null;
    };


    /**
     * Gets the categories object ordered by parent category
     * @returns {*}
     */
    _pub.getOrderedCategories = function () {
        return _orderedCategories;
    };

    /**
     * Gets the parent categories in alphabetical order
     * @returns {*}
     */
    _pub.getParentCategories = function () {
        return _parentCategories;
    };

    /**
     * Takes a parent category name and replaces spaces with dashes and & with 'and'
     * @param name
     * @returns {string}
     */
    _pub.getParentCategorySlug = (name) => {
        return name.replace(/\s+/g, '-').replace('&', 'and').toLowerCase();
    };

    /**
     * Gets a nested categories object by parent category
     */
    _pub.getCategoryObject = function () {
        return _categoryObject;
    };

    /**
     * Gets a category by its id
     */
    _pub.getCategoryById = function (categoryId) {
        if (Object.keys(_categoryIdToObject).length === 0) {
            _categories.forEach(function (category) {
                _categoryIdToObject[category.id] = category;
            });
        }

        return categoryId in _categoryIdToObject ? _categoryIdToObject[categoryId] : false;
    };

    /**
     * Gets a category by its code
     *
     * @param {String} categoryCode the `category_code` of the category
     * to retrieve.
     * @returns {Promise} a promise that either resolves to the
     * category or `false`.
     */
    _pub.getCategoryByCode = function (categoryCode) {
        return _pub.afterInitialCategoriesLoads().then(function() {
            var categoryId = _categoryCodeToId[categoryCode];
            return _categoryIdToObject[categoryId] || false;
        });
    };

    /**
     * Gets the active category
     * @returns {*}
     */
    _pub.getActiveCategory = function () {
        if (_activeCategory === null && _categories) {
            _activeCategory = _pub.getDefaultCategory();
            if (typeof _activeCategory === 'undefined') {
                throw Error(
                    "No default active category set. Please set a " +
                    "`default_active_category` in the site settings "+
                    "admin (e.g. \"Washer\")."
                );
            }
        }

        return _activeCategory;
    };

    /**
     * Sets the active category
     * @returns {*}
     */
    _pub.setActiveCategory = function (categoryId) {
        var activeCategory = _pub.getCategoryById(categoryId);

        if (activeCategory) {
            _activeCategory = activeCategory;
        }
        // Triggers an update of the available filters based on the activeCategory
        $rootScope.$emit('categorySet', _activeCategory);
    };

    _pub.updateCategoriesIcons = function (categories) {
        /**
         * Listens for the event that is broadcasted when a user chooses a utility provider.
         * Checks to see if categories rebate icons have to be displayed, and calls
         * all registered events. Used for any site that uses the
         * `utility_rate_selector` or `utility_state_rate_selector`
         * feature.
         */
        $rootScope.$on('utilitySet', function (event, args) {
            _activeUtilityId = args.electrical_utility_id || _activeUtilityId;
            _pub.getCategories(_activeUtilityId).then(() => {
                UtilService.extendObj(categories, _pub.getOrderedCategories());
            });
        });
    };

    /**
     * Get subcategories for parent category
     * @param {String} categoryCode the `category_code` of the parent category
     * @returns Array
     */
    _pub.getCategoryChildren = function (categoryCode) {
        let children = [];
        Object.keys(_categoryObject).forEach((name) => {
             if (_pub.getParentCategorySlug(name) === categoryCode) {
                children = _categoryObject[name];
             }
        });
        return children;
    };

    _pub.updateCategoriesIcons(_categories);

    /**
     * Returns the given category's default_category_image category setting.
     * @param {Object} category 
     * @returns String
     */
    _pub.getCategoryImage = function (category) {
        return UtilService.getCategorySetting(category.id, 'default_category_image');
    };

    return _pub;
}

module.exports = CategoriesServiceProvider;